import { DatePipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import * as FileSaver from "file-saver";
import { Message, SelectItem } from "primeng/components/common/api";
import { AppConstants } from "../core/app.constants";
import { AuthAccessProvider } from "../core/core.auth";
import { BaseComponent, DetailedTask, Offer, UserContext } from "../core/core.component";
import { BusyProvider } from "../service/busy.service";
import { MessageProvider } from "../service/message.service";
import { OfferService } from "../service/offers.service";
import { ReportService } from "../service/report.service";
import { TaskService } from "../service/task.service";
import { ScadenzeFatturazioneService } from "../service/scadenze.service";


@Component({
    selector: "app-ticket-da-fatturare",
    templateUrl: "./ticket-da-fatturare.component.html",
    styleUrls: ["./ticket-da-fatturare.component.css"],
})
export class TicketDaFatturareComponent extends BaseComponent implements OnInit {
    userContext: UserContext;

    tickets: DetailedTask[] = [];
    filteredTickets: DetailedTask[] = [];
    loading: boolean = false;

    offers: Offer[] = [];
    commesseOptions: SelectItem[] = [];
    commesseFiltered: SelectItem[] = [];
    filterRefOrder: string = null;

    showFilter: boolean = false;
    sortField: string = null;
    sortAsc: boolean = false;

    showExportDialog: boolean = false;
    exportDtFattura: Date = null;
    exportCloseTasks: boolean = false;
    exportValidationMessage: string = null;
    locale: any = AppConstants.ITA_CALENDAR_CONF;

    msgs: Message[] = [];

    ticketFilterMode: string = 'P';
    uiTicketFilterMode: string = 'P';

    groupByOffers: boolean = false;
    selectedTickets = new Set<string>();
    selectAll: boolean = false;

    constructor(
        protected authAccess: AuthAccessProvider,
        protected router: Router,
        protected messageProvider: MessageProvider,
        private taskService: TaskService,
        private offerService: OfferService,
        private reportService: ReportService,
        private busyProvider: BusyProvider,
        private datePipe: DatePipe,
        private scadenzeService: ScadenzeFatturazioneService,
    ) {
        super(authAccess, router, messageProvider);
    }

    ngOnInit() {
        this.userContext = this.authAccess.getUserContext();
        if (this.userContext == null) {
            this.authAccess.invalidate();
            return;
        }
        this.loadOffers();

    }
    isSelected(ticket: DetailedTask): boolean {
        return this.selectedTickets.has(ticket.issueNo);
    }
    toggleTicket(ticket: DetailedTask): void {
        if (this.selectedTickets.has(ticket.issueNo)) {
            this.selectedTickets.delete(ticket.issueNo);
        } else {
            this.selectedTickets.add(ticket.issueNo);
        }
        this.selectAll = this.filteredTickets.every(t => this.selectedTickets.has(t.issueNo));
    }
    toggleSelectAll(): void {
        if (this.selectAll) {
            this.filteredTickets.forEach(t => this.selectedTickets.add(t.issueNo));
        } else {
            this.selectedTickets.clear();
        }
    }

    getSelectedTickets(): DetailedTask[] {
        return this.filteredTickets.filter(t => this.selectedTickets.has(t.issueNo));
    }

    loadOffers() {
        this.offerService.getOffers(this.userContext.srmToken, false).subscribe(
            (result) => {
                if (result.message) {
                    try {
                        const offers = JSON.parse(result.message);
                        this.offers = offers || [];

                        this.commesseOptions = this.offers
                            .map(o => ({ label: o.dsofferta, value: o.reforder }))
                            .sort((a, b) => (a.value || '').localeCompare(b.value || '', 'it', { sensitivity: 'base' }));
                    } catch (e) {
                        console.error("Errore nel parsing JSON offerte:", e);
                    }
                }
                this.loadTickets();
            },
            (error) => {
                this.messageProvider.notifyError(error);
                this.loadTickets();
            }
        );
    }

    loadTickets() {
        this.loading = true;
        this.tickets = [];
        this.filteredTickets = [];

        this.taskService.searchTaskByStatus(this.userContext.srmToken, "COMPLETATO").subscribe(
            (result) => {
                this.tickets = result && result.issues ? result.issues : [];

                this.refreshFilteredTickets();
                if (this.sortField) this.sort(this.sortField, this.sortAsc);
                this.loading = false;
            },
            (error) => {
                this.messageProvider.notifyError(error);
                this.loading = false;
            }
        );
    }

    aggiorna() {
        this.loadOffers();
    }

    filterOffer(event) {
        this.commesseFiltered = AppConstants.filterSuggestions(this.commesseOptions, event.query, item => [item.label, item.value]);
    }

    onSelectOffer(offer: any) {
        this.filterRefOrder = offer.value;
    }

    toggleFilter() {
        this.uiTicketFilterMode = this.ticketFilterMode;
        this.showFilter = !this.showFilter;
    }

    applyFilter() {
        if (this.ticketFilterMode != this.uiTicketFilterMode) this.ticketFilterMode = this.uiTicketFilterMode;
        this.refreshFilteredTickets();
        this.showFilter = false;
    }

    removeFilter() {
        this.filterRefOrder = null;
        this.uiTicketFilterMode = 'P';
        this.ticketFilterMode = 'P';
        this.refreshFilteredTickets();
        this.showFilter = false;
    }

    private refreshFilteredTickets() {
        if (this.ticketFilterMode === 'NC') {
            const allRefOrders = new Set(this.offers.map(o => o.reforder));
            this.filteredTickets = this.tickets.filter(t => !t.reforder || !allRefOrders.has(t.reforder));
        } else if (this.filterRefOrder) {
            const ticketsFiltro = this.tickets.filter(t => t.reforder === this.filterRefOrder);
            if (this.ticketFilterMode === 'P') {
                const reforderP = new Set(this.offers.filter(o => o.tipologia == "P").map(o => o.reforder));
                this.filteredTickets = ticketsFiltro.filter(t => reforderP.has(t.reforder));
            } else {
                this.filteredTickets = ticketsFiltro;
            }
        } else {
            if (this.ticketFilterMode === 'P') {
                const reforderP = new Set(this.offers.filter(o => o.tipologia == "P").map(o => o.reforder));
                this.filteredTickets = this.tickets.filter(t => reforderP.has(t.reforder));
            } else {
                this.filteredTickets = this.tickets.slice();
            }
        }

        const visibleIssueNos = new Set(this.filteredTickets.map(t => t.issueNo));
        this.selectedTickets.forEach(issueNo => {
            if (!visibleIssueNos.has(issueNo)) {
                this.selectedTickets.delete(issueNo);
            }
        });
        this.selectAll = this.filteredTickets.length > 0
            && this.filteredTickets.every(t => this.selectedTickets.has(t.issueNo));
    }

    sort(field: string, asc?: boolean) {
        if (field === this.sortField && (typeof asc === 'undefined' || asc == !this.sortAsc)) {
            this.filteredTickets = this.filteredTickets.reverse();
            this.sortAsc = !this.sortAsc;
        } else {
            this.sortField = field;
            this.sortAsc = true;
            this.filteredTickets = this.filteredTickets.sort((a1, a2) => {
                let res = 0;
                if (this.sortField === 'ticket') {
                    res = this.compareTickets(a1.issueNo, a2.issueNo);
                } else if (this.sortField === 'descrizione') {
                    res = this.compareStrings(a1.title, a2.title);
                } else if (this.sortField === 'commessa') {
                    res = this.compareStrings(a1.reforder, a2.reforder);
                }
                return this.sortAsc ? res : -res;
            });
        }
    }

    private compareStrings(s1: string, s2: string): number {
        const a = s1 || '';
        const b = s2 || '';
        return a.localeCompare(b, 'it', { sensitivity: 'base', numeric: true });
    }

    private compareTickets(t1: string, t2: string): number {
        if (!t1 && !t2) return 0;
        if (!t1) return -1;
        if (!t2) return 1;
        const r = /^([A-Z]{3})-(\d+)$/i;
        const m1 = t1.match(r);
        const m2 = t2.match(r);
        if (!m1 || !m2) {
            return t1 > t2 ? 1 : -1;
        }
        const text1 = m1[1].toUpperCase();
        const text2 = m2[1].toUpperCase();
        if (text1 !== text2) {
            return text1 > text2 ? 1 : -1;
        }
        const num1 = Number(m1[2]);
        const num2 = Number(m2[2]);
        return num1 - num2;
    }

    confirmTicketExport() {
        if (this.filteredTickets.length === 0) return;
        this.busyProvider.showBusyIndicator();
        this.messageProvider.clearAll();

        const parameters = new Object();
        parameters['exportReport'] = "Export";
        if (this.selectedTickets.size > 0 || this.ticketFilterMode === 'NC') {
            const tickets = this.selectedTickets.size > 0 ? this.getSelectedTickets() : this.filteredTickets;
            parameters['listaTicket'] = tickets.map(ticket => ticket.id).join(",");
        } else {
            parameters['refOrder'] = this.filterRefOrder;
            parameters['isOnlyPlafond'] = (this.ticketFilterMode === 'P').toString();
        }

        this.reportService.exportReport(this.userContext.srmToken, 'com.netsitech.srm.excel.generator.TicketDaFatturareReportGenerator', parameters).subscribe(
            data => {
                const filename = "Export_Ticket_Da_Fatturare_" + new Date().getTime() + ".xlsx";
                this.download(data, filename);
                this.busyProvider.hideBusyIndicator();
            },
            error => {
                this.busyProvider.hideBusyIndicator();
                this.messageProvider.notifyError(error);
            }
        );
    }

    openExportDialog() {
        if (this.ticketFilterMode === 'NC' || this.filteredTickets.length === 0) return;
        this.exportDtFattura = null;
        this.exportCloseTasks = false;
        this.groupByOffers = false;
        this.exportValidationMessage = null;
        this.showExportDialog = true;
    }

    closeExportDialog() {
        this.showExportDialog = false;
        this.exportValidationMessage = null;
    }

    confirmExport() {
        this.exportValidationMessage = null;
        if (this.exportDtFattura == null) {
            this.exportValidationMessage = "Inserire la data fattura";
            return;
        }

        this.busyProvider.showBusyIndicator();
        this.messageProvider.clearAll();

        const dtFattura = this.exportDtFattura;
        const closeTasks = this.exportCloseTasks;
        const ticketsSnapshot = this.selectedTickets.size > 0
            ? this.getSelectedTickets().slice()
            : this.filteredTickets.slice();

        const parameters = new Object();
        parameters['exportReport'] = "Report";
        parameters['groupByOffer'] = this.groupByOffers.toString();
        parameters['dataFattura'] = this.datePipe.transform(dtFattura, "yyyy-MM-dd");
        if (this.selectedTickets.size > 0) {
            parameters['listaTicket'] = ticketsSnapshot.map(ticket => ticket.id).join(",");
        } else {
            parameters['refOrder'] = this.filterRefOrder;
            parameters['isOnlyPlafond'] = (this.ticketFilterMode === 'P').toString();
        }
        this.reportService.exportReport(this.userContext.srmToken, 'com.netsitech.srm.excel.generator.TicketDaFatturareReportGenerator', parameters).subscribe(
            data => {
                const filename = "Report_Ticket_Da_Fatturare_" + new Date().getTime() + ".xlsx";
                this.download(data, filename);
                this.showExportDialog = false;
                if (closeTasks && ticketsSnapshot.length > 0) {
                    this.createScadenzeFatturazioneAfterExportAndCloseTicket(ticketsSnapshot, dtFattura);
                } else {
                    this.busyProvider.hideBusyIndicator();
                    this.msgs = [{ severity: 'success', summary: 'OK', detail: 'Report esportato correttamente' }];
                }
            },
            error => {
                this.busyProvider.hideBusyIndicator();
                this.showExportDialog = false;
                this.messageProvider.notifyError(error);
            }
        );
    }

    private createScadenzeFatturazioneAfterExportAndCloseTicket(tickets: DetailedTask[], dtFattura: Date) {
        const senzaOfferta: string[] = [];
        const ticketsConOfferta: DetailedTask[] = [];

        tickets.forEach(ticket => {
            const offerta = this.offers.find(off => ticket.reforder == off.reforder);
            if (offerta) {
                ticketsConOfferta.push(ticket);
            } else {
                senzaOfferta.push(ticket.issueNo);
            }
        });

        const messages: Message[] = [];
        if (senzaOfferta.length > 0) {
            messages.push({
                severity: 'warn', summary: 'Attenzione',
                detail: `Offerta non trovata per ${senzaOfferta.length} ticket: ` + senzaOfferta.join(', ')
            });
        }

        if (ticketsConOfferta.length === 0) {
            this.busyProvider.hideBusyIndicator();
            this.msgs = messages;
            return;
        }

        const ticketIds = ticketsConOfferta.map(t => t.id);
        this.scadenzeService.insertScadenzeTicketDaFatturare(
            this.userContext.srmToken, ticketIds, dtFattura, this.groupByOffers
        ).subscribe(
            result => {
                this.busyProvider.hideBusyIndicator();
                try {
                    const payload = JSON.parse(result.message);
                    const scadenzeInserite = payload.scadenzeInserite || 0;
                    const ticketChiusi = payload.ticketChiusi || 0;
                    const daChiudereManuali: string[] = payload.ticketDaChiudereManualmente || [];

                    if (daChiudereManuali.length > 0) {
                        messages.push({
                            severity: 'warn', summary: 'Attenzione',
                            detail: `Scadenze inserite: ${scadenzeInserite}, ticket chiusi: ${ticketChiusi}. Da chiudere manualmente: ` + daChiudereManuali.join(', ')
                        });
                    } else {
                        messages.push({
                            severity: 'success', summary: 'OK',
                            detail: `Scadenze inserite: ${scadenzeInserite}, ticket chiusi: ${ticketChiusi}`
                        });
                    }
                    this.msgs = messages;
                } catch (e) {
                    console.error("Errore parsing risposta insertScadenzeTicketDaFatturare:", e);
                    messages.push({ severity: 'error', summary: 'Errore', detail: 'Risposta non valida dal server' });
                    this.msgs = messages;
                }
                this.loadTickets();
            },
            error => {
                this.busyProvider.hideBusyIndicator();
                this.messageProvider.notifyError(error);
                this.loadTickets();
            }
        );
    }

    private download(data: any, filename: string) {
        const blob = new Blob([data], { type: "application/vnd.ms-excel" });
        FileSaver.saveAs(blob, filename);
    }

    estimatedInDays(t: DetailedTask): number {
        const et = t && t.estimatedTime;
        if (et == null) {
            return null;
        }
        const months = et.months || 0;
        const weeks = et.weeks || 0;
        const days = et.days || 0;
        const hours = et.hours || 0;
        const totalHours = Math.round((hours / 8) * 100) / 100; //arrotondo alla seconda cifra decimale
        const total = months * 30 + weeks * 7 + days + totalHours;
        return total;
    }
}



// WEBPACK FOOTER //
// ./src/app/ticket-da-fatturare/ticket-da-fatturare.component.ts